import { useQuery } from '@vrplatform/data';
import { Edit3Icon, Icon, RouteIcon } from '@vrplatform/icons';
import {
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import {
  parseAsString,
  useQueryState,
  useQueryStates,
} from '@vrplatform/query-params';
import { Box, Tooltip } from '@mantine/core';
import { deleteCheckListing } from '@vrplatform/ui-common';
import { type ReactNode, useMemo } from 'react';
import {
  EllipsisMenuSourceDownloadItem,
  HiddenFeatureIndicator,
} from '../../../components';
import type { ListingData } from '../detail/useListingDetailViewQuery';

type Props = {
  listing: {
    listingId: string;
    isPmsListing: boolean;
    ownerStatements: number;
    listingConnections: number;
    deactivation: ListingData['deactivation'] | undefined;
  };
  handlers: {
    openDeleteModal: () => void;
    openMigrationModal: () => void;
  };
  hideEdit?: boolean;
};

export const LegacyListingEllipsisMenuItems = (props: Props) => {
  const {
    listing: { listingId },
    hideEdit = false,
  } = props;

  const [, setListingId] = useQueryState('listing_activity', parseAsString);

  const [, setView] = useQueryStates({
    view: parseAsString,
    listing: parseAsString,
  });

  return (
    <>
      {!hideEdit && (
        <EllipsisMenuItem
          onClick={() => setView({ listing: listingId, view: 'edit' })}
          customIcon={<Edit3Icon size={16} />}
        >
          Edit listing
        </EllipsisMenuItem>
      )}

      <EllipsisMenuItem
        customIcon={<Icon icon="HistoryIcon" size={16} />}
        onClick={() => setListingId(listingId, { history: 'push' })}
      >
        View activity
      </EllipsisMenuItem>

      <EllipsisMenuItem
        onClick={props.handlers.openMigrationModal}
        customIcon={<RouteIcon size={16} />}
      >
        Migrate Listing
      </EllipsisMenuItem>

      <EllipsisMenuDivider />

      <Delete {...props} />

      <HiddenFeatureIndicator permission="vrp-admin">
        <EllipsisMenuCopyItem value={listingId} />

        <EllipsisMenuSourceDownloadItem
          fileName={listingId}
          where={{ listing: { id: { _eq: listingId } } }}
        />
      </HiddenFeatureIndicator>
    </>
  );
};

const Delete = ({
  listing: { isPmsListing, listingConnections, ownerStatements, listingId },
  handlers: { openDeleteModal },
}: {
  listing: {
    listingId: string;
    isPmsListing: boolean;
    ownerStatements: number;
    listingConnections: number;
  };
  handlers: {
    openDeleteModal: () => void;
    openMigrationModal: () => void;
  };
}) => {
  const { data, isLoading: loading } = useQuery(
    (q, { listingId }) => deleteCheckListing(q, { listingId }),
    {
      queryKey: 'listing',
      variables: {
        listingId,
      },
    }
  );

  const canBeDeleted = !!data?.canDelete;

  const deleteTooltipLabel = useMemo(() => {
    if (isPmsListing) {
      return 'This listing is synced with a PMS and cannot be deleted.';
    } else {
      if (ownerStatements && listingConnections)
        return 'This listing has owner statements and listing connections. Remove them first to delete this listing.';

      if (ownerStatements)
        return 'Remove the owner statements from this listing to delete it.';

      if (listingConnections)
        return 'Remove the listing connections from this listing to delete it.';

      return null;
    }
  }, [listingConnections, ownerStatements, isPmsListing]);

  const disableRemoving = !!listingConnections || isPmsListing || !canBeDeleted;

  return (
    <DisabledTooltip label={deleteTooltipLabel} disabled={!disableRemoving}>
      <Box>
        <EllipsisMenuDangerItem
          onClick={openDeleteModal}
          loading={loading}
          disabled={disableRemoving}
        >
          Delete listing
        </EllipsisMenuDangerItem>
      </Box>
    </DisabledTooltip>
  );
};

const DisabledTooltip = ({
  disabled,
  label,
  children,
}: {
  disabled: boolean;
  label: ReactNode;
  children: ReactNode;
}) => {
  return (
    <Tooltip label={label} withArrow withinPortal disabled={disabled}>
      <Box
        sx={{
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
